import { Icon, IconButton, InputBase, Popover } from "@mui/material";
import { useEffect, useRef, useState } from "react";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";

function WhatsAppMessageComposer({ conversation, sendMessage, onTemplateMessage }) {
  const [message, setMessage] = useState("");
  const [media, setMedia] = useState([]);
  const [emojiCategories, setEmojiCategories] = useState([]);
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [voiceNote, setVoiceNote] = useState(null);
  const [voiceNoteUrl, setVoiceNoteUrl] = useState("");
  const [recordingDuration, setRecordingDuration] = useState(0);

  const fileInputRef = useRef(null);
  const composerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordingStreamRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingInProgressRef = useRef(false);
  const discardRecordingRef = useRef(false);
  const recordingTimerRef = useRef(null);
  const recordingStartTimeRef = useRef(null);

  useEffect(() => {
    fetch("/assets/emojis.json")
      .then((response) => response.json())
      .then((data) => {
        setEmojiCategories(data);
      })
      .catch(() => {
        setEmojiCategories([]);
      });
  }, []);

  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }

      if (mediaRecorderRef.current?.state !== "inactive") {
        mediaRecorderRef.current?.stop();
      }

      recordingStreamRef.current?.getTracks().forEach((track) => {
        track.stop();
      });

      if (voiceNoteUrl) {
        URL.revokeObjectURL(voiceNoteUrl);
      }
    };
  }, [voiceNoteUrl]);

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
  };

  const clearRecordingTimer = () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  const stopRecordingStream = () => {
    if (recordingStreamRef.current) {
      recordingStreamRef.current.getTracks().forEach((track) => {
        track.stop();
      });

      recordingStreamRef.current = null;
    }
  };

  const clearVoiceNote = () => {
    if (voiceNoteUrl) {
      URL.revokeObjectURL(voiceNoteUrl);
    }

    setVoiceNote(null);
    setVoiceNoteUrl("");
    setRecordingDuration(0);
  };

  const startRecording = async () => {
    if (!conversation || recordingInProgressRef.current || isRecording || voiceNote) {
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      alert("Voice recording is not supported by this browser.");
      return;
    }

    recordingInProgressRef.current = true;
    discardRecordingRef.current = false;
    recordingChunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      if (!recordingInProgressRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      recordingStreamRef.current = stream;

      const supportedMimeType = [
        "audio/ogg;codecs=opus",
        "audio/mp4",
        "audio/webm;codecs=opus",
        "audio/webm",
      ].find((type) => MediaRecorder.isTypeSupported(type));

      const recorder = supportedMimeType
        ? new MediaRecorder(stream, { mimeType: supportedMimeType })
        : new MediaRecorder(stream);

      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordingChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        clearRecordingTimer();

        const shouldDiscard = discardRecordingRef.current;
        const chunks = recordingChunksRef.current;
        const mimeType = recorder.mimeType || "audio/webm";

        if (!shouldDiscard && chunks.length > 0) {
          const extension = mimeType.includes("ogg")
            ? "ogg"
            : mimeType.includes("mp4")
            ? "m4a"
            : "webm";

          const recordedFile = new File(chunks, `voice-note-${Date.now()}.${extension}`, {
            type: mimeType,
          });

          const previewUrl = URL.createObjectURL(recordedFile);

          setVoiceNote(recordedFile);
          setVoiceNoteUrl(previewUrl);
        }

        recordingChunksRef.current = [];
        mediaRecorderRef.current = null;
        recordingInProgressRef.current = false;

        stopRecordingStream();
        setIsRecording(false);
      };

      recorder.onerror = () => {
        clearRecordingTimer();
        recordingInProgressRef.current = false;
        setIsRecording(false);
        stopRecordingStream();
      };

      recordingStartTimeRef.current = Date.now();
      setRecordingDuration(0);
      setIsRecording(true);

      recordingTimerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - recordingStartTimeRef.current) / 1000);

        setRecordingDuration(elapsed);
      }, 250);

      recorder.start();
    } catch (error) {
      clearRecordingTimer();
      recordingInProgressRef.current = false;
      setIsRecording(false);
      stopRecordingStream();

      alert("Unable to access the microphone. Please check your permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
  };

  const cancelVoiceNote = () => {
    if (isRecording || recordingInProgressRef.current) {
      discardRecordingRef.current = true;
      recordingInProgressRef.current = false;
      clearRecordingTimer();

      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      } else {
        stopRecordingStream();
        setIsRecording(false);
      }

      setRecordingDuration(0);
      return;
    }

    clearVoiceNote();
  };

  const sendVoiceNote = () => {
    if (!voiceNote || !conversation) {
      return;
    }

    sendMessage({
      content_type: "media",
      content: "",
      media: [voiceNote],
    });

    clearVoiceNote();
  };

  const handleSend = () => {
    if (!message.trim() && media.length === 0) {
      return;
    }

    sendMessage({
      content_type: media.length > 0 ? "media" : "text",
      content: message.trim(),
      media: media,
    });

    setMessage("");
    setMedia([]);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleMediaChange = (event) => {
    const files = Array.from(event.target.files || []);

    if (files.length > 0) {
      setMedia(files);
    }

    event.target.value = "";
  };

  const openMediaPicker = () => {
    if (!conversation) {
      return;
    }

    fileInputRef.current?.click();
  };

  const openEmojiPicker = (event) => {
    if (!conversation) {
      return;
    }

    setEmojiAnchor(event.currentTarget);
  };

  const closeEmojiPicker = () => {
    setEmojiAnchor(null);
  };

  const addEmoji = (emoji) => {
    setMessage((currentMessage) => currentMessage + emoji);
  };

  return (
    <MDBox
      ref={composerRef}
      sx={{
        width: "100%",
        borderTop: "1px solid #e0e0e0",
        backgroundColor: "#f0f2f5",
        px: 2,
        py: 1,
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
        onChange={handleMediaChange}
      />

      {media.length > 0 && (
        <MDBox
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
            px: 1,
          }}
        >
          <MDTypography
            variant="caption"
            sx={{
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {media.length} file{media.length > 1 ? "s" : ""} selected
          </MDTypography>

          <IconButton size="small" onClick={() => setMedia([])}>
            <Icon fontSize="small">close</Icon>
          </IconButton>
        </MDBox>
      )}

      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={closeEmojiPicker}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              width: composerRef.current?.offsetWidth || 360,
              maxWidth: composerRef.current?.offsetWidth || 360,
              height: 420,
              maxHeight: "calc(100vh - 32px)",
              p: 1,
              overflow: "hidden",
              backgroundColor: "#ffffff",
              opacity: 1,
            },
          },
        }}
      >
        <MDBox
          sx={{
            height: "100%",
            overflowY: "auto",
          }}
        >
          {emojiCategories.map((category) => (
            <MDBox
              key={category.category}
              sx={{
                mb: 1.5,
              }}
            >
              <MDTypography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#667781",
                  px: 1,
                  py: 0.5,
                }}
              >
                {category.category}
              </MDTypography>

              <MDBox
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(12, 1fr)",
                }}
              >
                {category.emojis.map((item) => (
                  <button
                    key={`${category.category}-${item.title}-${item.emoji}`}
                    type="button"
                    title={item.title}
                    onClick={() => addEmoji(item.emoji)}
                    style={{
                      width: "40px",
                      height: "40px",
                      padding: 0,
                      margin: 0,
                      border: "none",
                      borderRadius: "4px",
                      background: "transparent",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: 1,
                      filter: "none",
                    }}
                    onMouseEnter={(event) => {
                      event.currentTarget.style.backgroundColor = "#f0f2f5";
                    }}
                    onMouseLeave={(event) => {
                      event.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <span
                      style={{
                        display: "block",
                        fontFamily:
                          '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif',
                        fontSize: "22px",
                        lineHeight: "22px",
                        fontWeight: "normal",
                        opacity: 1,
                        filter: "none",
                        color: "initial",
                        textShadow: "none",
                        WebkitTextFillColor: "initial",
                      }}
                    >
                      {item.emoji}
                    </span>
                  </button>
                ))}
              </MDBox>
            </MDBox>
          ))}
        </MDBox>
      </Popover>

      {(isRecording || voiceNote) && (
        <MDBox
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2,
            py: 1.5,
            mb: 1,
            backgroundColor: "#ffffff",
            borderRadius: "16px",
            boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
          }}
        >
          {isRecording ? (
            <>
              <MDBox
                sx={{
                  width: 10,
                  height: 10,
                  flexShrink: 0,
                  borderRadius: "50%",
                  backgroundColor: "#e53935",
                  animation: "voicePulse 1.2s infinite",
                  "@keyframes voicePulse": {
                    "0%, 100%": { opacity: 1 },
                    "50%": { opacity: 0.3 },
                  },
                }}
              />

              <MDBox sx={{ flex: 1 }}>
                <MDTypography sx={{ fontSize: "14px", fontWeight: 600 }}>
                  Recording voice note
                </MDTypography>

                <MDTypography sx={{ fontSize: "12px", color: "text.secondary" }}>
                  {formatDuration(recordingDuration)}
                </MDTypography>
              </MDBox>

              <IconButton size="medium" onClick={cancelVoiceNote} title="Cancel recording">
                <Icon>delete_outline</Icon>
              </IconButton>

              <IconButton
                size="medium"
                onClick={stopRecording}
                title="Finish recording"
                sx={{
                  backgroundColor: "#e7fce3",
                  color: "#16803c",
                  "&:hover": { backgroundColor: "#d4f7ce" },
                }}
              >
                <Icon>stop</Icon>
              </IconButton>
            </>
          ) : (
            <>
              <Icon sx={{ color: "#16803c", fontSize: 28 }}>graphic_eq</Icon>

              <MDBox sx={{ flex: 1, minWidth: 0 }}>
                <MDTypography sx={{ fontSize: "13px", fontWeight: 600, mb: 0.5 }}>
                  Voice note · {formatDuration(recordingDuration)}
                </MDTypography>

                <audio
                  controls
                  src={voiceNoteUrl}
                  style={{
                    display: "block",
                    width: "100%",
                    height: "36px",
                  }}
                />
              </MDBox>

              <IconButton size="medium" onClick={cancelVoiceNote} title="Cancel voice note">
                <Icon>delete_outline</Icon>
              </IconButton>

              <IconButton
                size="medium"
                onClick={sendVoiceNote}
                title="Send voice note"
                sx={{
                  backgroundColor: "#16803c",
                  color: "#ffffff",
                  "&:hover": { backgroundColor: "#116b30" },
                }}
              >
                <Icon>send</Icon>
              </IconButton>
            </>
          )}
        </MDBox>
      )}

      <MDBox
        sx={{
          display: "flex",
          alignItems: "flex-end",
          width: "100%",
          backgroundColor: "#ffffff",
          borderRadius: "24px",
          px: 1,
          py: 0.5,
        }}
      >
        <IconButton
          size="small"
          onClick={openEmojiPicker}
          disabled={!conversation || isRecording || Boolean(voiceNote)}
          sx={{ mr: 0.5 }}
        >
          <Icon>emoji_emotions</Icon>
        </IconButton>

        <IconButton
          size="small"
          onClick={openMediaPicker}
          disabled={!conversation || isRecording || Boolean(voiceNote)}
          sx={{ mr: 0.5 }}
        >
          <Icon>attach_file</Icon>
        </IconButton>

        {/* <IconButton
          size="small"
          onClick={onTemplateMessage}
          disabled={!conversation || isRecording || Boolean(voiceNote)}
          sx={{ mr: 0.5 }}
        >
          <Icon>description</Icon>
        </IconButton> */}

        <IconButton
          size="small"
          onClick={isRecording ? stopRecording : startRecording}
          disabled={!conversation || Boolean(voiceNote)}
          title={isRecording ? "Stop recording" : "Record voice note"}
          sx={{
            mr: 0.5,
            color: isRecording ? "error.main" : "inherit",
          }}
        >
          <Icon>{isRecording ? "stop_circle" : "mic_none"}</Icon>
        </IconButton>

        <InputBase
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          onKeyDown={handleKeyDown}
          disabled={!conversation || isRecording || Boolean(voiceNote)}
          multiline
          maxRows={5}
          placeholder={conversation ? "Type a message" : "Select a conversation"}
          sx={{
            flex: 1,
            fontSize: "14px",
            px: 1,
            py: 0.75,
            lineHeight: 1.5,
          }}
        />

        <IconButton
          size="small"
          onClick={handleSend}
          disabled={
            !conversation ||
            isRecording ||
            Boolean(voiceNote) ||
            (!message.trim() && media.length === 0)
          }
          sx={{ ml: 0.5 }}
        >
          <Icon>send</Icon>
        </IconButton>
      </MDBox>
    </MDBox>
  );
}

export default WhatsAppMessageComposer;
